import {
  AddOn_schema,
  AdminFees_schema,
  Bounty_schema,
  EventDataSchema,
  NO_VALUE_PLACEHOLDER,
  ReEntry_schema,
  Rebuy_schema,
} from '../services/Schemas/EventSchema';

export type EventGameType = 'holdem' | 'omaha' | 'dealerschoice';
export type EventPrivacy = 'public' | 'private' | 'invitational';
export type EventGameLimit = 'no-limit' | 'pot-limit' | 'fixed-limit';

export type AdminFee = { payee: string; percent: number };
export type StartingChip = { key: number; value: string };
export type BlindLevelDuration = { key: number; value: string };

export type Location = {
  address: {
    label: string;
    value: string;
  };
  lat: number;
  lng: number;
};

export type Rebuy = {
  isRebuys: boolean;
  rebuysAllowed?: number | string;
  rebuyCutoff?: number;
  rebuyPrice?: number;
  rebuyChips?: number;
};

export type AddOn = {
  isAddOn: boolean;
  addOnPrice?: number;
  addOnAmount?: number;
};

export type BigBlindAnte = {
  isBigBlindAnte: boolean;
  bigBlindAnteRoundStart?: number;
};

export type LateEntry = {
  isLateEntry: boolean;
  lateEntryCutoff?: number;
};

export type ReEntry = {
  isReEntry: boolean;
  reEntryCutoff?: number;
  reEntries?: number;
  reEntryPrice?: number;
};

export type Bounty = {
  isBounty: boolean;
  bountyPrice?: number;
  bountyAmount?: number;
};

export interface Event {
  id?: string;
  name: string;
  desc: string;
  imageUrl?: string;
  imageFile?: FileList | null;
  privacy: EventPrivacy;
  venueId?: string;
  structureId?: string;
  charityId?: string;
  charityName?: string;
  charityPct?: number;
  licenseId?: string;
  licenseType?: string;
  isRecurring?: boolean;
  recurringFrequency?: string;
  additionalGames?: string[];
  cashGameStakes?: string;
  eventNotes?: string;
  startDateTime: any;
  // Dealer assignment
  status?: string;
  archived_at?: string;
  dealerUid?: string;
  dealerStatus?: 'none' | 'pending' | 'accepted' | 'declined' | 'standby' | 'timed_out';
  dealerInviteDeadline?: string;    // ISO string — TD sets response deadline
  dealerStandbyCallByTime?: string; // ISO string — dealer sets standby expiry
  dealerInviteUrgent?: boolean;     // auto-flagged if deadline < 6hrs from now
  location: Location;
  timezone: any;
  currency: string;
  adminFees: AdminFee[];
  gameType: EventGameType;
  gameLimit: EventGameLimit;
  maxPlayers: number;
  buyIn: number;
  startingChips: number;
  blindLevelDuration: number;
  blindStructure?: {
    level: string;
    label?: string;
    smallBlind?: number;
    bigBlind?: number;
    ante?: number;
    duration: number;
  }[];
  rebuys: Rebuy;
  addOn: AddOn;
  bigBlindAnte: BigBlindAnte;
  lateEntry: LateEntry;
  reEntry: ReEntry;
  bounty: Bounty;
  // Seating
  seatingMode?: 'random' | 'manual';
  tableCount?: number;
  seatingLog?: {
    generatedAt: string;
    mode: 'random' | 'manual';
    playersSeated: number;
    tableCount: number;
  }[];
}

export const schemaToEvent = (data: EventDataSchema, id?: any): Event => {
  const event = {
    id: id,
    name: data.name,
    desc: data.desc,
    privacy: data.privacy,
    venueId: data.venue_id,
    structureId: data.structure_id,
    charityId: data.charity_id,
    charityName: data.charity_name,
    charityPct: data.charity_pct,
    licenseId: data.license_id,
    licenseType: data.license_type,
    isRecurring: data.is_recurring,
    recurringFrequency: data.recurring_frequency,
    additionalGames: data.additional_games ?? [],
    cashGameStakes: data.cash_game_stakes,
    eventNotes: data.event_notes,
    startDateTime: data.start,
    dealerUid: data.dealer_uid,
    dealerStatus: data.dealer_status ?? 'none',
    dealerInviteDeadline: data.dealer_invite_deadline,
    dealerStandbyCallByTime: data.dealer_standby_call_by_time,
    dealerInviteUrgent: data.dealer_invite_urgent ?? false,
    location: data.location,
    timezone: data.timezone,
    currency: data.currency,
    adminFees: schemaToAdminFees(data.admin_fees),
    gameType: data.game_type,
    gameLimit: data.game_limit,
    maxPlayers: data.max_players,
    seatingMode: data.seating_mode ?? 'random',
    tableCount: data.table_count,
    seatingLog: data.seating_log ?? [],
    buyIn: data.buy_in,
    startingChips: data.starting_chips,
    blindLevelDuration: data.blind_level_duration,
    blindStructure: data.blind_structure
      ? Object.keys(data.blind_structure)
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => (data.blind_structure as any)[key])
      : [],
    rebuys: schemaToRebuy(data.rebuy),
    addOn: schemaToAddOn(data.add_on),
    bigBlindAnte: schemaToBigBlindAnte(data.big_blind_ante_round_start),
    lateEntry: schemaToLateEntry(data.late_entry_cutoff),
    reEntry: schemaToReEntry(data.reentry),
    bounty: schemaToBounty(data.bounty)
  } as Event;

  // Optional values
  if (data.image_url) {
    event.imageUrl = data.image_url;
  }
  return event;
};

export const schemaToAdminFees = (
  adminFeesSchema: AdminFees_schema
): AdminFee[] => {
  const adminFees: AdminFee[] = [];

  if (!adminFeesSchema) {
    return adminFees;
  }
  for (const [key, value] of Object.entries(adminFeesSchema)) {
    adminFees.push({ payee: key, percent: value.percent });
  }
  return adminFees;
};

export const schemaToRebuy = (rebuySchema: Rebuy_schema): Rebuy => {
  const { rebuys_allowed, rebuy_cutoff, rebuy_price, rebuy_chips } =
    rebuySchema;
  if (
    !rebuys_allowed ||
    rebuys_allowed === NO_VALUE_PLACEHOLDER ||
    !rebuy_cutoff ||
    rebuy_cutoff === NO_VALUE_PLACEHOLDER ||
    !rebuy_price ||
    rebuy_price === NO_VALUE_PLACEHOLDER ||
    !rebuy_chips ||
    rebuy_chips === NO_VALUE_PLACEHOLDER
  ) {
    return { isRebuys: false };
  } else {
    return {
      isRebuys: true,
      rebuysAllowed: rebuys_allowed,
      rebuyCutoff: rebuy_cutoff,
      rebuyPrice: rebuy_price,
      rebuyChips: rebuy_chips,
    };
  }
};

export const schemaToAddOn = (addOnSchema: AddOn_schema): AddOn => {
  const { add_on_price, add_on_chips } = addOnSchema;
  if (!add_on_price ||
    add_on_price === NO_VALUE_PLACEHOLDER ||
    !add_on_chips ||
    add_on_chips === NO_VALUE_PLACEHOLDER) {
    return {
      isAddOn: false
    }
  } else {
    return {
      isAddOn: true,
      addOnAmount: add_on_chips,
      addOnPrice: add_on_price
    }
  }
}

export const schemaToBigBlindAnte = (big_blind_ante_round_start: number): BigBlindAnte => {
  if (!big_blind_ante_round_start || big_blind_ante_round_start === NO_VALUE_PLACEHOLDER) {
    return {
      isBigBlindAnte: false,
    }
  } else {
    return {
      isBigBlindAnte: true,
      bigBlindAnteRoundStart: big_blind_ante_round_start
    }
  }
}

export const schemaToLateEntry = (late_entry_cutoff: number): LateEntry => {
  if (!late_entry_cutoff || late_entry_cutoff === NO_VALUE_PLACEHOLDER) {
    return {
      isLateEntry: false
    }
  } else {
    return {
      isLateEntry: true,
      lateEntryCutoff: late_entry_cutoff
    }
  }
}

export const schemaToReEntry = (reEntrySchema: ReEntry_schema): ReEntry => {
  const { reentries, reentry_cutoff, reentry_price } = reEntrySchema;
  if (!reentries || reentries === NO_VALUE_PLACEHOLDER
    || !reentry_cutoff || reentry_cutoff === NO_VALUE_PLACEHOLDER
    || !reentry_price || reentry_price === NO_VALUE_PLACEHOLDER
  ) {
    return {
      isReEntry: false
    }
  } else {
    return {
      isReEntry: true,
      reEntries: reentries,
      reEntryCutoff: reentry_cutoff,
      reEntryPrice: reentry_price
    }
  }
}

export const schemaToBounty = (bountySchema: Bounty_schema): Bounty => {
  const { bounty_price, bounty_amount } = bountySchema;
  if (!bounty_price || bounty_price === NO_VALUE_PLACEHOLDER
    || !bounty_amount || bounty_amount === NO_VALUE_PLACEHOLDER
  ) {
    return {
      isBounty: false
    }
  } else {
    return {
      isBounty: true,
      bountyAmount: bounty_amount,
      bountyPrice: bounty_price
    }
  }
}
